import { IntlShape } from 'react-intl';

import { humanizeKey } from 'containers/OfferLetterTemplates/constants';

import messages from './messages';
import { conditionEntries } from './visibility';
import { ConditionalBlock, ConditionOperator, TypedInput, VisibilityCondition } from './types';

/**
 * The generated lines under a Send Offer input.
 *
 * Both are built from the payload, never hard-coded: the blocks a data key
 * drives come from `conditional_blocks[].included_when`, and what an edit does
 * to the record behind a field comes from its `written_back` and `source`.
 */

/** "the value is …" and its siblings. `in` reads the same as `equals`. */
type ClauseKind = 'is' | 'isNot' | 'contains' | 'greater' | 'less' | 'present' | 'blank';

const CLAUSE_KIND: Record<ConditionOperator, ClauseKind> = {
  equals: 'is',
  in: 'is',
  not_equals: 'isNot',
  not_in: 'isNot',
  contains: 'contains',
  greater_than: 'greater',
  less_than: 'less',
  present: 'present',
  blank: 'blank',
};

/** The rule line lists at most this many clauses, then "and N more". */
export const MAX_CLAUSES = 3;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A stored value as the control beside the line shows it; see `valueLabeler`. */
export type ValueLabel = (value: string) => string;

const asStored: ValueLabel = value => value;

type ListType = 'disjunction' | 'conjunction' | 'unit';

/**
 * `A, B or C` / `A, B and C` / `A, B, C`. English keeps the ticket's grammar,
 * with no serial comma, which `Intl.ListFormat` would add; every other locale
 * takes its own separators and conjunction from `formatList`.
 */
const joinList = (intl: IntlShape, items: string[], type: ListType): string => {
  if (!intl.locale.toLowerCase().startsWith('en')) {
    return intl.formatList(items, { type, style: type === 'unit' ? 'short' : 'long' });
  }
  if (type === 'unit' || items.length <= 1) return items.join(', ');
  const head = items.slice(0, -1).join(', ');
  const last = items[items.length - 1];
  return intl.formatMessage(type === 'disjunction' ? messages.listOr : messages.listAnd, { head, last });
};

/** The data key's own type, which decides how a value reads. */
type KeyType = string | null | undefined;

/**
 * A value as the recruiter reads it: a date key's `2027-01-01` as
 * `Jan 1, 2027`, anything else as its control shows it. A value holding a
 * comma (`Dallas, TX`) is quoted, or the joined list would read it as two
 * entries.
 */
const displayValue = (intl: IntlShape, value: string, keyType: KeyType, label: ValueLabel): string => {
  if (keyType === 'date' && ISO_DATE.test(value)) {
    return intl.formatDate(`${value}T00:00:00Z`, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }
  const shown = label(value);
  return shown.includes(',') ? intl.formatMessage(messages.quotedValue, { value: shown }) : shown;
};

const clauseFor = (
  intl: IntlShape,
  kind: ClauseKind,
  values: string[],
  keyType: KeyType,
  label: ValueLabel,
): string => {
  const shown = values.map(value => displayValue(intl, value, keyType, label));
  const or = joinList(intl, shown, 'disjunction');
  switch (kind) {
    case 'is':
      return intl.formatMessage(messages.clauseIs, { values: or });
    case 'isNot':
      return intl.formatMessage(messages.clauseIsNot, { values: or });
    case 'contains':
      return intl.formatMessage(messages.clauseContains, { values: or });
    case 'greater':
      return intl.formatMessage(keyType === 'date' ? messages.clauseAfter : messages.clauseGreater, { values: or });
    case 'less':
      return intl.formatMessage(keyType === 'date' ? messages.clauseBefore : messages.clauseLess, { values: or });
    case 'present':
      return intl.formatMessage(messages.clausePresent);
    case 'blank':
    default:
      return intl.formatMessage(messages.clauseBlank);
  }
};

interface DrivenBlock {
  name: string;
  conditions: VisibilityCondition[];
  clause: string;
}

/**
 * One block's clause for this key. Its conditions are OR-combined: positive
 * conditions of one kind merge their values with "or", and each negative
 * condition keeps its own clause, because "not CA" or "not WA" is not
 * "not CA or WA". Clauses join with ", or", and a condition on another key
 * appends that other answers can include the block.
 */
const blockClause = (
  intl: IntlShape,
  conditions: VisibilityCondition[],
  hasOtherKeys: boolean,
  keyType: KeyType,
  label: ValueLabel,
): string => {
  const groups: { kind: ClauseKind; values: string[] }[] = [];
  conditions.forEach(condition => {
    const kind = CLAUSE_KIND[condition.operator];
    const merged = kind === 'isNot' ? undefined : groups.find(group => group.kind === kind);
    const group = merged ?? { kind, values: [] };
    if (!merged) groups.push(group);
    conditionEntries(condition).forEach(value => {
      if (!group.values.includes(value)) group.values.push(value);
    });
  });

  const clauses = groups.map(({ kind, values }) => clauseFor(intl, kind, values, keyType, label));
  const clause = clauses.join(intl.formatMessage(messages.clauseSeparatorOr));

  return hasOtherKeys ? intl.formatMessage(messages.clauseOtherAnswers, { clause }) : clause;
};

const isSingleValueMatch = (block: DrivenBlock): boolean =>
  block.conditions.length === 1 &&
  CLAUSE_KIND[block.conditions[0].operator] === 'is' &&
  conditionEntries(block.conditions[0]).length === 1;

/**
 * The rule line under a condition input's label: which blocks this data key
 * drives, and on which values. Null when it drives none.
 */
export const conditionSubtext = (
  intl: IntlShape,
  dataKey: string,
  blocks: ConditionalBlock[],
  keyType: KeyType,
  label: ValueLabel = asStored,
): string | null => {
  const driven: DrivenBlock[] = blocks.flatMap(block => {
    const conditions = block.included_when.filter(condition => condition.data_key === dataKey);
    if (conditions.length === 0) return [];
    const hasOtherKeys = block.included_when.some(condition => condition.data_key !== dataKey);
    return [
      {
        name: block.label || humanizeKey(block.key),
        conditions,
        clause: blockClause(intl, conditions, hasOtherKeys, keyType, label),
      },
    ];
  });

  if (driven.length === 0) return null;

  if (driven.length === 1) {
    return intl.formatMessage(messages.subtextOneBlock, { name: driven[0].name, clause: driven[0].clause });
  }

  const values = driven.map(block => conditionEntries(block.conditions[0])[0]);
  if (driven.every(isSingleValueMatch) && new Set(values).size === values.length) {
    const shown = values.map(value => displayValue(intl, value, keyType, label));
    return intl.formatMessage(messages.subtextOnePerValue, { values: joinList(intl, shown, 'unit') });
  }

  if (driven.every(block => block.clause === driven[0].clause)) {
    return intl.formatMessage(messages.subtextSharedClause, {
      names: joinList(intl, driven.map(block => block.name), 'conjunction'),
      clause: driven[0].clause,
    });
  }

  const shown = driven.slice(0, MAX_CLAUSES).map(block =>
    intl.formatMessage(messages.subtextBlockClause, { name: block.name, clause: block.clause }),
  );
  const rest = driven.length - shown.length;
  const clauses = shown.join(intl.formatMessage(messages.subtextClauseSeparator));
  return rest > 0
    ? intl.formatMessage(messages.subtextBlocksMore, { clauses, count: rest })
    : intl.formatMessage(messages.subtextBlocks, { clauses });
};

/** A null `written_back` is a field mapped to nothing: no record to talk about. */
export type WriteBackState = Pick<TypedInput, 'written_back' | 'source' | 'source_value'>;

/**
 * The note under an edited input whose value is not saved back. Null when the
 * value is saved back, or not changed from what its record holds.
 */
export const documentOnlySubtext = (
  intl: IntlShape,
  field: WriteBackState,
  current: string,
  label: ValueLabel = asStored,
): string | null => {
  if (field.written_back !== false) return null;
  const original = field.source_value ?? '';
  if (current === original) return null;

  if (field.source == null) return intl.formatMessage(messages.documentOnly);

  return original.trim() === ''
    ? intl.formatMessage(messages.documentOnlyNotUpdated, { source: field.source })
    : intl.formatMessage(messages.documentOnlyStays, { source: field.source, value: label(original) });
};
